import { useCallback, useEffect, useRef } from 'react';
import { useTour } from '@reactour/tour';
import { differenceInMinutes, parseISO } from 'date-fns';
import { useExperimentFlags } from '@hungryroot/experiments';
import { useCartStore } from '@hungryroot/global-state';
import {
  useNextOrderV3,
  usePricingServiceCustomerPlansPrice,
} from '@hungryroot/rest-hooks';
import {
  consumePendingCartOpenAfterFirstOrderTour,
  hasPendingCartOpenAfterFirstOrderTour,
  OnboardingProgress,
  useOnboardingProgress,
} from '../../components/cart/OnboardingPopover/OnboardingProgress';

/** Used as a placeholder so the tour doesn't target or highlight any elements. */
// This is needed here to prevent circular deps.
export const NON_EXISTENT_STEP_SELECTOR =
  '#non-existent-selector-used-as-placeholder';

/**
 * Makes use of some onboarding progress state to determine
 * whether to start the first order tour.
 */
const validOnboardingProgressForTourStart = new Set<string>([
  // ShowCustomizeModal is no longer written by new code but is kept here so
  // users with the stale value from a pre-deploy session still trigger the tour.
  OnboardingProgress.ShowCustomizeModal,
  OnboardingProgress.ShowFirstPopover,
  OnboardingProgress.ShowSecondPopover,
]);

/**
 * Triggers the first order tour if the user meets the criteria. Can be called
 * multiple times but the tour will only be triggered once. Best triggered on the
 * component highlighted on the first step of the tour.
 */
export function useTriggerFirstOrderTour() {
  const { order } = useNextOrderV3();
  const { getOnboardingProgress } = useOnboardingProgress(order) || {};
  const { setIsOpen, isOpen, steps } = useTour();
  const triggeredTourRef = useRef(isOpen);

  const onboardingProgress = getOnboardingProgress?.();

  const { progressiveOnboardingExp227, isLoading: isExperimentFlagsLoading } =
    useExperimentFlags([227, 198]);
  const { isLoading: isLoadingPlansPrice } =
    usePricingServiceCustomerPlansPrice();
  const isTourContentReady = !isExperimentFlagsLoading && !isLoadingPlansPrice;
  const shouldDisableTourForExp227 =
    isExperimentFlagsLoading || progressiveOnboardingExp227?.isVariant;

  const shouldStartTour =
    !shouldDisableTourForExp227 &&
    isTourContentReady &&
    typeof onboardingProgress === 'string' &&
    validOnboardingProgressForTourStart.has(onboardingProgress) &&
    order &&
    differenceInMinutes(parseISO(order.cutoff_datetime), new Date()) > 0 &&
    order.sequence === 1;

  const triggerFirstOrderTour = useCallback(() => {
    if (shouldStartTour && !triggeredTourRef.current && steps.length > 0) {
      let selector = steps[0].selector as string;

      if (selector === NON_EXISTENT_STEP_SELECTOR) {
        selector = steps[1].selector as string;
      }

      if (document.querySelector(selector)) {
        setIsOpen(true);
        triggeredTourRef.current = true;
      }
    }
  }, [setIsOpen, shouldStartTour, steps]);

  useEffect(() => {
    if (!isTourContentReady || order === undefined) {
      return;
    }

    if (!shouldStartTour && hasPendingCartOpenAfterFirstOrderTour()) {
      if (consumePendingCartOpenAfterFirstOrderTour()) {
        useCartStore.getState().setCartActive(true);
      }
    }
  }, [isTourContentReady, order, shouldStartTour]);

  return {
    triggerFirstOrderTour,
  };
}
